import {translate} from 'views/module';

import moment from 'moment-timezone';

// Week keys in financial snapshots use the Payment-FE completion tracker grammar,
// e.g. '2026Week13' resolved against the ISO week-year.
const WEEK_OF_FORMAT = 'GGGG[Week]WW';

const countFormatter = new Intl.NumberFormat('en-CA', {maximumFractionDigits: 0});
const hoursFormatter = new Intl.NumberFormat('en-CA', {maximumFractionDigits: 2});
const currencyFormatter = new Intl.NumberFormat('en-CA', {style: 'currency', currency: 'CAD'});

// Missing means null/undefined/NaN. Zero is a legitimate value and must never be
// treated as missing.
const isMissingValue = (value) => {
  return value === null || value === undefined || (typeof value === 'number' && Number.isNaN(value));
};

const unavailableText = () => {
  return translate('metrics.ClientMetrics.Unavailable');
};

const formatCount = (value) => {
  return isMissingValue(value) ? unavailableText() : countFormatter.format(value);
};

const formatHours = (value) => {
  return isMissingValue(value) ? unavailableText() : hoursFormatter.format(value);
};

const formatCurrency = (value) => {
  return isMissingValue(value) ? unavailableText() : currencyFormatter.format(value);
};

// Gross margin is stored as a ratio (0.4057 -> '40.6%').
const formatMargin = (value) => {
  return isMissingValue(value) ? unavailableText() : `${(value * 100).toFixed(1)}%`;
};

const formatByKind = (value, kind) => {
  if (kind === 'currency') {
    return formatCurrency(value);
  }

  if (kind === 'hours') {
    return formatHours(value);
  }

  if (kind === 'margin') {
    return formatMargin(value);
  }

  return formatCount(value);
};

const weekOfToMoment = (weekOf) => {
  if (!weekOf) {
    return null;
  }

  const weekMoment = moment.utc(weekOf, WEEK_OF_FORMAT);

  return weekMoment.isValid() ? weekMoment.startOf('isoWeek') : null;
};

const formatWeekLabel = (weekOf) => {
  const weekMoment = weekOfToMoment(weekOf);

  return weekMoment ? weekMoment.format('MMM D') : unavailableText();
};

const formatWeekOfDate = (weekOf) => {
  const weekMoment = weekOfToMoment(weekOf);

  return weekMoment ? weekMoment.format('MMM D, YYYY') : unavailableText();
};

const formatIsoDate = (value) => {
  if (!value) {
    return unavailableText();
  }

  const dateMoment = moment.utc(value);

  return dateMoment.isValid() ? dateMoment.format('MMM D, YYYY') : unavailableText();
};

// Week keys are zero padded ('2026Week02'), so a plain string sort is chronological.
const sortWeekKeysAscending = (weeks) => {
  return Object.keys(weeks || {}).sort((left, right) => left.localeCompare(right));
};

const formatSlipCompletion = (completeCount, incompleteCount) => {
  if (isMissingValue(completeCount) || isMissingValue(incompleteCount)) {
    return unavailableText();
  }

  return translate('metrics.ClientMetrics.WeeklyBreakdown.SlipCompletion', {
    complete: countFormatter.format(completeCount),
    total: countFormatter.format(completeCount + incompleteCount),
  });
};

// Builds the formatted value/previous strings and the delta chip for a metric card.
// goodDirection states which way the metric should move ('up', 'down', or 'neutral')
// so the chip tone reflects business meaning instead of raw sign. No chip is shown
// when either side of the comparison is missing.
const buildComparison = (currentValue, previousValue, {kind = 'count', goodDirection = 'neutral'} = {}) => {
  const value = formatByKind(currentValue, kind);
  const previous = formatByKind(previousValue, kind);

  if (isMissingValue(currentValue) || isMissingValue(previousValue)) {
    return {
      value,
      previous,
      chip: null,
    };
  }

  const delta = currentValue - previousValue;
  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  let tone = 'neutral';

  if (delta !== 0 && goodDirection !== 'neutral') {
    const isGood = goodDirection === 'up' ? delta > 0 : delta < 0;

    tone = isGood ? 'positive' : 'negative';
  }

  let label = '';

  if (kind === 'margin') {
    // Margin comparisons are expressed in percentage points, not relative growth.
    const deltaPoints = delta * 100;
    const pointsPrefix = deltaPoints > 0 ? '+' : deltaPoints < 0 ? '-' : '';

    label = translate('metrics.ClientMetrics.MarginPoints', {
      value: `${pointsPrefix}${Math.abs(deltaPoints).toFixed(1)}`,
    });
  } else {
    let deltaPercentage = 0;

    if (previousValue === 0 && currentValue !== 0) {
      deltaPercentage = currentValue > 0 ? 100 : -100;
    } else if (previousValue !== 0) {
      deltaPercentage = (delta / Math.abs(previousValue)) * 100;
    }

    const percentagePrefix = deltaPercentage > 0 ? '+' : deltaPercentage < 0 ? '-' : '';
    const absolutePercentage = Math.abs(deltaPercentage);
    const formattedPercentage = Math.abs(deltaPercentage % 1) < 0.01 ?
      absolutePercentage.toFixed(0) :
      absolutePercentage.toFixed(1);

    label = `${percentagePrefix}${formattedPercentage}%`;
  }

  return {
    value,
    previous,
    chip: {
      direction,
      tone,
      label,
    },
  };
};

export default {
  isMissingValue,
  formatCount,
  formatHours,
  formatCurrency,
  formatMargin,
  formatByKind,
  formatWeekLabel,
  formatWeekOfDate,
  formatIsoDate,
  formatSlipCompletion,
  sortWeekKeysAscending,
  weekOfToMoment,
  buildComparison,
};
